import { Container, Graphics } from "pixi.js"

import { interpolateColor } from "./dial"

// EQUIPMENT CONDITION, drawn.
//
// Weapons wear (`INVENTORY_ITEM_DURABILITY_EVENT` -> `updateInventoryItemDurability`) and break at
// zero, taking the player's only axe with them mid-swing. Until now the only way to know that was
// coming was to open the pack, hover the item and read "38% durable (worn)" in the corner of a menu
// -- a number you had to go and ask for, about the thing in your hand.
//
// This module is the one place that decides what wear LOOKS like, because it is drawn in four:
// the readied-item tray (gui/equipped.ts), every inventory/chest/trade slot and the drag ghost
// (gui/modern_inventory_dialog.ts), and the item menu's own readout. Those live in two different
// palettes -- the tray is warm timber, the panel is cool slate -- so the gauge is deliberately
// palette-NEUTRAL: a dark track and a green/amber/red fill read as the same object in both, and
// condition is not a thing that should change meaning depending on which window it is in.
//
// Three rules carry the whole design:
//
//   1. SILENCE AT FULL. Nothing is drawn above PRISTINE. A fresh pack would otherwise be a grid of
//      identical green bars, which is noise wearing the costume of information -- the bar appearing
//      at all is the first half of the message.
//   2. LENGTH IS THE MESSAGE, COLOUR IS THE ALARM. The track is always full width, so what is left
//      is read against the item's whole life rather than as "a short bar"; the ramp is redundant
//      encoding on top, which is what keeps it legible to a colour-blind player.
//   3. ONE STATE SHOUTS. Only "fragile" (<= 25%, the tier the server already names) gets a bloom,
//      and only in the tray does that bloom breathe. A warning on every worn item is wallpaper.
//
// Everything is plain fills -- no filters, per [[game-budget-hardware-target]]; a soft edge here is
// a fatter, fainter copy of the same capsule.

/**
 * At or above this, the gauge is not drawn at all.
 *
 * Not 1.0: `computeItemConditionPct` divides an integer hp by an integer max, so a 100/100 metal
 * weapon lands exactly on 1 but a 70/70 wooden one can come back 0.9999999999999999 off the float
 * divide, and a hairline bar under a brand-new axe is a bug report.
 */
export const PRISTINE = 0.995

/** The tier that gets the bloom. Matches `Mechanics.item.durability` "fragile". */
export const FRAGILE = 0.25

/**
 * The ramp, as stops rather than one green-to-red mix.
 *
 * A single lerp end to end puts a muddy olive at the halfway mark -- which reads as neither healthy
 * nor hurt, and halfway is exactly where a player starts thinking about a replacement -- and it
 * also drags "new" territory a long way towards yellow, so a 90% axe looked tired. The stops are
 * placed so the colour and the WORD the server's tiers would give it (75/50/25) agree at a glance:
 * green above three quarters, amber through the middle, red by the time it is fragile.
 */
const RAMP: [number, number][] = [
    [0, 0xcf3b32],
    // Red by the time the gauge is in the warning tier, rather than somewhere on the way there:
    // drawn with the red stop only at zero, a weapon two swings from breaking was still ORANGE,
    // which is the colour of "getting on a bit" and not of "this is about to leave your hand".
    [0.22, 0xe2543f],
    [0.45, 0xf0a63c],
    // A bright yellow between amber and green. Without it the mix sags through an unsaturated
    // khaki right where most of a player's gear lives, and a half-worn axe looked dirty rather
    // than half worn.
    [0.68, 0xdcc94e],
    [0.85, 0x8ecf5c],
    [1, 0x5fd16e],
]
const clamp01 = (value: number): number => (value < 0 ? 0 : value > 1 ? 1 : value)

/** Fill colour for a condition fraction. Continuous, so the gauge never "jumps a tier". */
export const conditionColor = (pct: number): number => {
    const p = clamp01(pct)
    for (let i = 1; i < RAMP.length; i++) {
        const [at, color] = RAMP[i]
        if (p <= at) {
            const [from, fromColor] = RAMP[i - 1]
            return interpolateColor(fromColor, color, (p - from) / (at - from))
        }
    }
    return RAMP[RAMP.length - 1][1]
}

/** True when a gauge should be drawn at all for this fraction. Undefined = the item cannot wear. */
export const showsWear = (pct?: number): boolean =>
    pct !== undefined && Number.isFinite(pct) && pct < PRISTINE

// The empty part of the gauge. Near-black, because at slot scale it is read against the well it
// sits in -- the tray's warm black and the panel's slate both -- and it has to be the hole in both.
const TRACK = 0x0b0a09
const TRACK_ALPHA = 0.78
// The item menu needs a lighter one. Its card is already near-black (COLOR.cardFill 0x11141c), so
// the slot track vanished into it and the gauge lost its far end: what was LEFT could be seen and
// what was GONE could not, which is half a gauge.
const CARD_TRACK = 0x272e3d

// Resting alpha of the fragile bloom, and the band it breathes across in the tray.
const BLOOM_REST = 0.38
const BLOOM_LOW = 0.2
const BLOOM_HIGH = 0.62
const BLOOM_PERIOD = 1400

/** Fraction of the track a gauge's fill covers, floored so the last sliver is still a visible dot. */
const fillWidth = (p: number, w: number, h: number): number => Math.max(h, w * p)

/**
 * The soft red halo under a fragile gauge, or undefined above the warning tier.
 *
 * It hugs the FILL, not the track. Drawn the track's full length -- the first thing tried -- it was
 * a long dull slab the colour of dried blood lying under the gauge, which read as a nearly-full bar
 * in a worrying colour: the exact opposite of what it is there to say. Around the stub that is left
 * it is a coal instead, and the darkness to its right is the part that is gone.
 */
const makeBloom = (pct: number, x: number, y: number, w: number, h: number): Graphics | undefined => {
    const p = clamp01(pct)
    if (p > FRAGILE) {
        return undefined
    }
    const bloom = new Graphics()
    bloom.beginFill(interpolateColor(conditionColor(p), 0xffffff, 0.15), 1)
    bloom.drawRoundedRect(x - 2.5, y - 2.5, fillWidth(p, w, h) + 5, h + 5, h / 2 + 2.5)
    bloom.endFill()
    bloom.alpha = BLOOM_REST
    return bloom
}

/**
 * The capsule gauge itself, in whatever box the caller gives it. Used at slot scale (a 3-4px bar in
 * the well's bottom gutter) and at card scale (a 6px bar across the item menu). The bloom, where
 * there is one, is a separate node the caller stacks UNDER this -- pixi draws a Graphics' own
 * geometry before its children, so a halo added to this one would sit on top of the gauge it is
 * meant to be glowing behind.
 */
const drawCapsule = (g: Graphics, pct: number, x: number, y: number, w: number, h: number,
    { sheen = true, ticks = false, track = TRACK, trackAlpha = TRACK_ALPHA }:
        { sheen?: boolean; ticks?: boolean; track?: number; trackAlpha?: number } = {},
) => {
    const p = clamp01(pct)
    const r = h / 2
    const color = conditionColor(p)
    const fillW = fillWidth(p, w, h)

    g.beginFill(track, trackAlpha)
    g.drawRoundedRect(x, y, w, h, r)
    g.endFill()

    g.beginFill(color, 1)
    g.drawRoundedRect(x, y, fillW, h, r)
    g.endFill()

    // Sheen along the top half, which is what makes the fill read as a lit tube instead of a flat
    // swatch of colour. Skipped on the 3px bars, where "half" is a third of the bar and the lighter
    // band just desaturates it.
    if (sheen && h >= 4) {
        const sheenH = h / 2 - 0.75
        g.beginFill(interpolateColor(color, 0xffffff, 0.45), 0.2)
        g.drawRoundedRect(x + 1, y + 0.75, Math.max(1, fillW - 2), sheenH, sheenH / 2)
        g.endFill()
    }

    // Engraved tier marks at the thresholds the server's own words turn on (75/50/25), so the
    // "Sturdy" printed beside the gauge and the length of the gauge agree with each other. Card
    // scale only -- at 22px wide they are three scratches.
    if (ticks) {
        g.beginFill(0x000000, 0.28)
        ;[0.25, 0.5, 0.75].forEach(at => g.drawRect(x + w * at - 0.5, y + 1, 1, h - 2))
        g.endFill()
    }
}

/**
 * The gauge for one item slot: a capsule seated in the bottom gutter of the well -- inside the
 * frame, under the art, clear of the floor -- at a size derived from the slot so the 32px docked
 * grids, the 40px tray and the 44px panel grid all get the same object rather than three.
 *
 * Returned as its own node, for the same reason the tray's quantity badge is: a Graphics draws its
 * own geometry BEFORE its children, so a bar drawn into the slot's Graphics would sit under the item
 * art -- and item art fills the well to within 4px, which is exactly where this lives.
 *
 * `rightInset` is room left for a quantity badge. Weapons are the only things that wear and weapons
 * do not stack, so in practice it is zero; it exists so that the day something stackable wears, the
 * badge is not sitting on top of the gauge.
 */
export class WearBar extends Container {
    private bloom?: Graphics
    private phase = 0

    private constructor(pct: number, size: number, rightInset: number) {
        super()
        const h = size >= 36 ? 4 : 3
        const side = Math.max(4, Math.round(size * 0.12))
        const w = Math.max(8, size - side * 2 - rightInset)
        // 4px off the floor of the well: the slot's own face is inset 2 and its rim is 1, so this
        // clears both and still reads as sitting INSIDE the frame rather than on its edge.
        const y = size - 4 - h
        this.bloom = makeBloom(pct, side, y, w, h)
        if (this.bloom) {
            this.addChild(this.bloom)
        }
        const body = new Graphics()
        drawCapsule(body, pct, side, y, w, h)
        this.addChild(body)
    }

    /** Undefined when the item cannot wear, or has not. Callers add the node if they get one. */
    static forSlot = (pct: number | undefined, size: number, rightInset = 0): WearBar | undefined =>
        showsWear(pct) ? new WearBar(pct!, size, rightInset) : undefined

    /** True while this gauge has something to animate, so an owner can skip the frame clock. */
    get breathing(): boolean {
        return !!this.bloom
    }

    /**
     * Breathe the fragile bloom. One alpha write per frame -- no geometry, no filters, nothing
     * rebuilt -- and a no-op on every bar that is not fragile, which is almost all of them.
     *
     * Driven off the frame delta rather than a wall clock, so a tab drawing no frames is not
     * quietly running a timer (see the hearts in gui/icon_meter.ts for the same argument).
     */
    animate = (deltaMs: number) => {
        if (!this.bloom) {
            return
        }
        this.phase = (this.phase + deltaMs) % BLOOM_PERIOD
        const wave = 0.5 - 0.5 * Math.cos((this.phase / BLOOM_PERIOD) * Math.PI * 2)
        this.bloom.alpha = BLOOM_LOW + (BLOOM_HIGH - BLOOM_LOW) * wave
    }
}

/** Height of the gauge drawn by {@link drawConditionGauge}. */
export const GAUGE_HEIGHT = 6

/**
 * The item menu's readout: the same capsule at a size you can actually read, with tier marks.
 *
 * Drawn into the card's own surface at (x, y); the caller owns the label and the figure beside it,
 * because it owns the card's type scale.
 */
export const drawConditionGauge = (g: Graphics, pct: number, x: number, y: number, w: number) => {
    drawCapsule(g, pct, x, y, w, GAUGE_HEIGHT, { ticks: true, track: CARD_TRACK, trackAlpha: 1 })
}
